
import { defineComponent, ref, onMounted, computed } from "vue";
import { MBDatatable } from "magicbean-saas-common";
import FilterDropdwon from "@/views/sales-channels/mobile-applications/FilterDropdown.vue";
import addAppModal from "@/views/sales-channels/mobile-applications/AddAppModal.vue";
import { MenuComponent } from "@/assets/ts/components";
import { ApiBase, ApiApp } from "@/core/api";
import moment from "moment";
import _ from "lodash";
import fileDownload from "js-file-download";
import { formatDateTime, setModuleBCN } from "@/core/directive/function/common";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

interface App {
  id: number;
  name: string;
  short_key: string;
  app_type: number;
  telephone: string;
  merchant: string;
}
interface SortOption {
  field: string;
  direction: string;
}

interface AppTyes {
  value: number;
  label: string;
}

interface FilterOption {
  field: string;
  value: string;
  from?: string;
  to?: number;
  condition?: string;
}

export default defineComponent({
  name: "mobile-applications",
  components: {
    MBDatatable,
    FilterDropdwon,
    addAppModal,
  },
  setup() {
    const { t } = useI18n();
    const route = useRoute();

    const loading = ref(true);
    const tableData = ref<Array<App>>([]);
    const currentPage = ref<number>(1);
    const total = ref<number>(1);
    const pageSize = ref<number>(10);
    const search = ref<string>("");
    const sortOptions = ref<Array<SortOption>>([]);
    const filterOptions = ref<Array<FilterOption>>([
      {
        field: "status",
        value: "10",
      },
    ]);
    const tableHeader = ref([
      {
        name: "App ID",
        key: "id",
        sortable: true,
      },
      {
        name: "App Name",
        key: "name",
        sortable: true,
      },
      {
        name: "App Type",
        key: "app_type",
        sortable: true,
      },
      {
        name: "Telephone",
        key: "telephone",
        sortable: true,
      },
      {
        name: "Parent Merchant",
        key: "merchant_id",
        sortable: true,
      },
      {
        name: "Status",
        key: "status",
        sortable: true,
      },
      {
        name: "Creation time",
        key: "created_at",
        sortable: true,
      },
      {
        name: t("common.action"),
        key: "actions",
      },
    ]);
    const disabledExport = ref(false);
    const appTypes = ref<Array<AppTyes>>([]);
    const getAppTypeOptions = () => {
      ApiBase.getTaggingData({ short_key: "global_app_type" })
        .then(({ data }) => {
          if (data.code == 0) {
            appTypes.value = data.data.items;
          }
        })
        .catch((error) => {
          console.log(error);
        });
    };

    const appTypeVal = computed(() => {
      return (val) => {
        let obj = appTypes.value.find((item) => {
          return item.value == val;
        });
        return obj?.label;
      };
    });

    onMounted(() => {
      MenuComponent.reinitialization();
      getAppTypeOptions();
      setModuleBCN(t, route);
      getAppLists(
        currentPage.value,
        pageSize.value,
        search.value,
        sortOptions.value,
        filterOptions.value
      );
    });

    const getAppLists = (
      page: number,
      pageSize: number,
      search?: string,
      sortOptions?: Array<SortOption>,
      filterOptions?: Array<FilterOption>
    ) => {
      loading.value = true;
      ApiApp.getAppList({
        page: page,
        page_size: pageSize,
        search_value: search,
        sort_orders: sortOptions,
        filter_group: filterOptions,
      })
        .then(({ data }) => {
          loading.value = false;
          if (data.code == 0) {
            tableData.value.splice(
              0,
              tableData.value.length,
              ...data.data.items
            );
            total.value = data.data.total;
          }
        })
        .catch((error) => {
          console.log(error);
        });
    };

    const onCurrentPageChange = (page) => {
      currentPage.value = page;
      getAppLists(
        page,
        pageSize.value,
        search.value,
        sortOptions.value,
        filterOptions.value
      );
    };

    const onRowsPerPageChange = (pagesize) => {
      pageSize.value = pagesize;
      getAppLists(
        currentPage.value,
        pagesize,
        search.value,
        sortOptions.value,
        filterOptions.value
      );
    };

    const handleFilter = (val) => {
      let filterArr: Array<FilterOption> = [];
      for (let item in val) {
        if (
          item == "merchant_id" ||
          item == "country_iso_2" ||
          item == "status"
        ) {
          filterArr.push({
            field: item,
            value: val[item],
            condition: "eq",
          });
        } else if (val[item] != "") {
          filterArr.push({
            field: item,
            value: val[item],
            condition: "like",
          });
        }
      }
      filterOptions.value.splice(0, filterOptions.value.length, ...filterArr);
      getAppLists(
        currentPage.value,
        pageSize.value,
        search.value,
        sortOptions.value,
        filterOptions.value
      );
    };

    const handleFilterReset = () => {
      resetFilter();
      getAppLists(
        currentPage.value,
        pageSize.value,
        search.value,
        sortOptions.value,
        filterOptions.value
      );
    };

    const resetFilter = () => {
      filterOptions.value.splice(0, filterOptions.value.length);
    };

    const onColumnSort = (val) => {
      let sortObj: SortOption = {
        field: val.columnName,
        direction: val.order,
      };
      sortOptions.value.splice(0, sortOptions.value.length, sortObj);
      getAppLists(
        currentPage.value,
        pageSize.value,
        search.value,
        sortOptions.value,
        filterOptions.value
      );
    };

    const debounceSearch = _.debounce(getAppLists, 1000);

    const searchItems = () => {
      debounceSearch(
        currentPage.value,
        pageSize.value,
        search.value,
        sortOptions.value,
        filterOptions.value
      );
    };

    const updateLists = () => {
      getAppLists(
        currentPage.value,
        pageSize.value,
        search.value,
        sortOptions.value,
        filterOptions.value
      );
    };

    const fileExport = () => {
      disabledExport.value = true;
      ApiApp.appPrint({
        search_value: search.value,
        filter_group: filterOptions.value,
        sort_orders: sortOptions.value,
        download_data_type: "stream",
      })
        .then((data) => {
          disabledExport.value = false;
          const disposition = data.headers["content-disposition"];
          fileDownload(data.data, disposition.split("filename=")[1]);
        })
        .catch((error) => {
          disabledExport.value = false;
          console.log(error);
        });
    };

    return {
      formatDateTime,
      loading,
      tableHeader,
      tableData,
      currentPage,
      total,
      pageSize,
      search,
      getAppLists,
      appTypes,
      appTypeVal,
      onCurrentPageChange,
      onRowsPerPageChange,
      searchItems,
      onColumnSort,
      handleFilter,
      handleFilterReset,
      updateLists,
      disabledExport,
      fileExport,
    };
  },
});
